import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";

interface TimeoutContextValue {
  timeoutRef: React.MutableRefObject<any>;
  lastCallbackIdRef: React.MutableRefObject<string | null>;
}

const TimeoutContext = createContext<TimeoutContextValue>({
  timeoutRef: { current: null },
  lastCallbackIdRef: { current: null },
});
/**
 * Custom hook to manage timeout calls with unique callback identifiers.
 *
 * @returns A function to schedule a timeout call.
 */
const useTimeoutCall = () => {
  const { timeoutRef, lastCallbackIdRef } = useContext(TimeoutContext);
  /**
   * Schedules a timeout call with a unique callback identifier.
   *
   * @param callbackId - A unique identifier for the callback.
   * @param callback - The function to execute after the timeout.
   * @param timeout - The duration of the timeout in milliseconds.
   */
  const timeoutCall = useCallback(
    (
      callbackId: string,
      callback: (...args: any[]) => void,
      timeout: number,
    ) => {
      if (lastCallbackIdRef.current !== callbackId) {
        lastCallbackIdRef.current = callbackId;
      } else {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        if (lastCallbackIdRef.current === callbackId) {
          callback();
          lastCallbackIdRef.current = null;
        }
      }, timeout);
    },
    [lastCallbackIdRef, timeoutRef],
  );
  return timeoutCall;
};

const TimeoutProvider = ({
  children,
}: {
  children: React.ReactNode | React.ReactNode;
}) => {
  const timeoutRef = useRef<any>(null);
  const lastCallbackIdRef = useRef<string | null>(null);

  useEffect(() => {
    return clearTimeout(timeoutRef.current);
  }, []);

  return (
    <TimeoutContext.Provider value={{ timeoutRef, lastCallbackIdRef }}>
      {children}
    </TimeoutContext.Provider>
  );
};

export { TimeoutProvider, useTimeoutCall };
